import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"

import type {
  BatteryPresentationShareStateDto,
  BatteryPresentationViewsDto,
} from "../dto/battery-presentation.dto"
import { batteryPresentationService } from "../services/battery-presentation.service"

export function useBatteryPresentationShare(
  proposalId: string | undefined,
): UseQueryResult<BatteryPresentationShareStateDto> {
  return useQuery({
    queryKey: queryKeys.batteryProposal.presentationLink(proposalId ?? ``),
    queryFn: () => {
      if (!proposalId) {
        throw new Error(`Battery-Only Proposal id is required`)
      }

      return batteryPresentationService.getShareState(proposalId)
    },
    enabled: Boolean(proposalId),
    retry: 1,
    // The lifecycle answer must come from the server every time the drawer
    // opens: another session may have unpublished the link, or the 30-day
    // expiry may have passed, since this cache was written.
    staleTime: 0,
  })
}

export function useBatteryPresentationViews(
  proposalId: string | undefined,
): UseQueryResult<BatteryPresentationViewsDto> {
  return useQuery({
    queryKey: queryKeys.batteryProposal.presentationViews(proposalId ?? ``),
    queryFn: () => {
      if (!proposalId) {
        throw new Error(`Battery-Only Proposal id is required`)
      }

      return batteryPresentationService.getViews(proposalId)
    },
    enabled: Boolean(proposalId),
    retry: 1,
    // Views arrive while the drawer is closed; a reopen must show them.
    staleTime: 0,
  })
}

/**
 * Publish and Unpublish both answer with the whole ShareState, so the response
 * replaces the cached state instead of triggering a re-read. Activity belongs
 * to the link, so a lifecycle change invalidates it.
 */
const useShareCommand = (
  run: (proposalId: string) => Promise<BatteryPresentationShareStateDto>,
): UseMutationResult<BatteryPresentationShareStateDto, unknown, string> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: run,
    onSuccess: (state, proposalId) => {
      queryClient.setQueryData(
        queryKeys.batteryProposal.presentationLink(proposalId),
        state,
      )
      void queryClient.invalidateQueries({
        queryKey: queryKeys.batteryProposal.presentationViews(proposalId),
      })
    },
    // A refused command means the client's picture of the link or its
    // readiness is behind the server's; read it again rather than keep it.
    onError: (_error, proposalId) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.batteryProposal.presentationLink(proposalId),
      })
    },
  })
}

export function usePublishBatteryPresentation(): UseMutationResult<
  BatteryPresentationShareStateDto,
  unknown,
  string
> {
  return useShareCommand((proposalId) =>
    batteryPresentationService.publish(proposalId),
  )
}

export function useUnpublishBatteryPresentation(): UseMutationResult<
  BatteryPresentationShareStateDto,
  unknown,
  string
> {
  return useShareCommand((proposalId) =>
    batteryPresentationService.unpublish(proposalId),
  )
}

/**
 * Generating the brochure PDF. The bytes are the answer, so nothing is cached;
 * a refusal means readiness moved under the client, so the share state is
 * re-read the way a refused publish re-reads it.
 */
export function useExportBatteryBrochure(): UseMutationResult<
  Blob,
  unknown,
  string
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (proposalId: string) =>
      batteryPresentationService.exportBrochure(proposalId),
    onError: (_error, proposalId) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.batteryProposal.presentationLink(proposalId),
      })
    },
  })
}
